import { useMemo, useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { CircleAlert, Info, ShieldCheck } from 'lucide-react';
import agent from '@root/agent';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { SEVERITY_COLORS } from '@root/utilities/pastelColors';
import { getResourceRiskType } from './findingConfig';

// "Resource risk analysis" card of the Findings overlay: why the finding's workload is risky, one line per
// factor, from the same resource risk API as the inventory overview's Risk Analysis card.

const VISIBLE_RISK_LINES = 5;
const SKELETON_WIDTHS = ['72%', '48%', '64%', '56%', '68%'];

// "has **1 critical** and **2 high** findings": the **runs** are the terms the line hinges on.
const RiskText = ({ text }) =>
  (text ?? '').split(/\*\*(.+?)\*\*/g).map((part, index) =>
    index % 2 === 0 ? (
      part
    ) : (
      <span key={index} className="FindingDetails__Emphasis">
        {part}
      </span>
    ),
  );

const RiskLine = ({ item }) => {
  const { icon: Icon, color, label } = getResourceRiskType(item.type);
  return (
    <li className="FindingDetails__RiskLine">
      <span className="FindingDetails__RiskIcon" style={{ color }} aria-label={label}>
        <Icon size={14} strokeWidth={2.25} />
      </span>
      <span>
        <RiskText text={item.text} />
      </span>
    </li>
  );
};

const RiskSkeleton = () => (
  <ul className="FindingDetails__RiskList">
    {SKELETON_WIDTHS.map((width, index) => (
      <li key={index} className="FindingDetails__RiskLine">
        <Skeleton variant="circular" width={14} height={14} sx={{ flexShrink: 0, marginTop: '3px' }} />
        <Skeleton width={width} height={20} />
      </li>
    ))}
  </ul>
);

const Note = ({ icon: Icon, color, children }) => (
  <p className="FindingDetails__Note">
    <Icon size={14} color={color} aria-hidden="true" />
    {children}
  </p>
);

// `workload` is the workload node's data from the findings graph API; the risk request waits for it.
const ResourceRisks = ({ accountId, workload, isLoadingWorkload }) => {
  const [showAll, setShowAll] = useState(false);

  const payload = useMemo(
    () => ({
      account_id: accountId,
      resource_id: workload?.workload_id,
      resource_name: workload?.workload_name,
      resource_type: workload?.workload_kind,
    }),
    [accountId, workload?.workload_id, workload?.workload_name, workload?.workload_kind],
  );

  const { data, isFetching, isError } = useEnhancedQuery(
    ['inventory_resource_overview_risk', payload],
    () => agent.Inventory_Resources.get_resource_risk(payload),
    { enabled: Boolean(payload.account_id && payload.resource_id) },
  );

  const items = data?.data?.items ?? [];
  // Factors the backend could not assess, as type keys (or objects carrying one).
  const unavailable = (data?.data?.unavailable ?? [])
    .map(entry => (typeof entry === 'string' ? entry : (entry?.type ?? entry?.name)))
    .filter(Boolean);

  if (isLoadingWorkload || (isFetching && !data)) return <RiskSkeleton />;
  if (!payload.resource_id) {
    return (
      <Note icon={Info}>No workload is linked to this finding, so its resource risks can&apos;t be assessed.</Note>
    );
  }

  const visible = showAll ? items : items.slice(0, VISIBLE_RISK_LINES);

  return (
    <>
      {items.length ? (
        <ul className="FindingDetails__RiskList">
          {visible.map((item, index) => (
            <RiskLine key={`${item.type}-${index}`} item={item} />
          ))}
        </ul>
      ) : isError ? (
        <Note icon={CircleAlert} color={SEVERITY_COLORS.critical}>
          Couldn&apos;t load the resource risk analysis.
        </Note>
      ) : (
        <Note icon={ShieldCheck} color={SEVERITY_COLORS.success}>
          No risk factors found for this resource.
        </Note>
      )}
      {items.length > VISIBLE_RISK_LINES && (
        <button type="button" className="FindingDetails__LinkButton" onClick={() => setShowAll(prev => !prev)}>
          {showAll ? 'Show less' : 'Show more'}
        </button>
      )}
      {unavailable.length > 0 && (
        <Note icon={Info}>Not assessed: {unavailable.map(type => getResourceRiskType(type).label).join(', ')}</Note>
      )}
    </>
  );
};

export default ResourceRisks;
